import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { getApi, useApi } from '@core/api'
import type { GetV1ItemOutput } from '@core/api/generated'
import { addSnack } from '@core/snack/snack-state'
import { ItemStatusMap } from '@core/types'
import { itemPollerKeys, useInvalidateItemPoller } from '@core/watch-item-status/item-poller'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useContentVersionState, type Slice } from './content-version-state'
import type { ItemActions } from './content-version-types'

type CreativitySliderValue = 'low' | 'mid' | 'high'

const creativitySliderMap: Record<number, CreativitySliderValue> = {
  0: 'low',
  50: 'mid',
  100: 'high',
}

export const mapCreativityToSlider = (value: number | null): CreativitySliderValue =>
  creativitySliderMap[value ?? 50] ?? 'mid'

export const notifyPossibleKeys = (options: { possible?: string }[], t: TFunction<'legacy'>) => {
  const hasPossibleKeys = options?.find((answer) => answer?.possible?.toUpperCase() === 'KEY')

  if (!hasPossibleKeys) {
    addSnack({
      message: t('author.possible_keys.not_found'),
      severity: 'info',
      checkboxKey: 'hidePossibleKeysNotFoundInfo',
    })
  }
}

type GenerateRationalesInput = {
  itemKey: string
  keysOnly: boolean
  option?: Record<string, any>
  generateRationaleFor?: number
  itemKeyCheck?: boolean
}

export type GenerateMoreItemsParams = {
  numberOfItems: string
  selectedFlavors: string[]
  selectedCreativity: number
}

export type RegenerateItemInput = {
  itemKey: string
  type: 'stem' | 'options' | 'parts' | 'part'
  removeOption?: number
}

type CreateItemActions = (
  rootId: string,
  get: Slice['getState'],
  set: Slice['setState'],
) => ItemActions

// Common actions shared between author-page and item-set surfaces.
export const createItemActions: CreateItemActions = (rootId, get) => ({
  async archiveItem(itemId, t) {
    try {
      const response = await getApi().archiveItem({
        path: { item_id: itemId },
      })

      if (response.error || !response.data?.id) {
        console.warn('Archive failed', { itemId, error: response.error })

        addSnack({
          message: t('author.item_actions.item_delete_error'),
          severity: 'error',
        })

        return { success: false }
      }

      addSnack({
        message: t('author.item_actions.item_deleted'),
        severity: 'success',
      })

      return { success: true }
    } catch (error) {
      addSnack({
        message: t('author.item_actions.item_delete_error'),
        severity: 'error',
      })

      throw error
    }
  },

  async discardItemsUnsavedContent() {
    const { instances, setInstance } = get()
    let instance = instances[rootId]

    const copy = { ...instance.items }
    const itemChanges = { ...instance.itemChanges }
    const itemKeys = Object.keys(copy).sort()

    for (const itemKey of itemKeys) {
      const item = copy[itemKey]

      if (!item.savedContent) continue

      copy[itemKey] = {
        ...item,
        currentContent: item.savedContent,
      }
      itemChanges[itemKey] = Object.keys(item.savedContent).reduce((acc, key) => {
        return {
          ...acc,
          [key]: true,
        }
      }, {})
    }

    setInstance(rootId, (prev) => {
      prev.items = copy
      prev.itemChanges = itemChanges
      prev.unsavedChanges = false
      prev.unsavedChangesFromLastTime = false
      prev.rootKey += 1
    })

    instance = get().instances[rootId]

    if (instance) {
      instance.validator(instance)
      instance.updater(instance)
      instance.updater.flush()
    }
  },
})

export const itemActionKeys = {
  all: ['item-action'],
  perItem: (itemId: string) => ['item-action', itemId],
  regenerate: (itemId: string) => ['item-action', itemId, 'regenerate'],
  possibleKeys: (itemId: string) => ['item-action', itemId, 'possible-keys'],
  save: (itemId: string) => ['item-action', itemId, 'save'],
  clone: (itemId: string) => ['item-action', itemId, 'clone'],
  rationales: (itemId: string) => ['item-action', itemId, 'rationales'],
  generateMath: (itemId: string) => ['item-action', itemId, 'generate-math'],
  generateMoreItems: (itemId: string) => ['item-action', itemId, 'generate-more-items'],
  biasCheck: (itemId: string) => ['item-action', itemId, 'bias-check'],
}

export const useRegenerateItem = (rootId: string, options?: { optimisticStatus?: boolean }) => {
  const api = useApi()
  const invalidatePoller = useInvalidateItemPoller()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: itemActionKeys.regenerate(rootId),

    onMutate: () => {
      queryClient.invalidateQueries({ queryKey: ['author-batches', 'details'] })
    },

    // @TODO:
    // https://finetune.atlassian.net/browse/FTG-6630
    // re-add this in destructuring: removeOption
    //
    mutationFn: async ({ itemKey, type: regenType }: RegenerateItemInput) => {
      const instance = useContentVersionState.getState().instances[rootId]
      const itemId = itemKey === 'root' ? rootId : itemKey

      try {
        await instance.flush()

        const response = await api.generate({
          body: {
            itemId,
            clearStem: regenType === 'stem',
            // shouldn't this be:
            // clearOptions: regenType === 'options'
            clearOptions: true,
            // @TODO:
            // https://finetune.atlassian.net/browse/FTG-6630
            // ...(removeOption !== null && removeOption !== undefined ? { removeOption, clearOptions: false } : {}),
          },
        })

        if (!response.data?.jobId) {
          throw new Error('No job id returned')
        }

        if (options?.optimisticStatus) {
          useContentVersionState.getState().setItem(rootId, itemId, (prev) => ({
            ...prev,
            status: ItemStatusMap.IN_PROGRESS,
          }))
        }

        await invalidatePoller(rootId)
      } catch (error) {
        console.debug(error, error?.message)
        console.error('regenerateItem error', error?.message, { rootId, itemId, type: regenType })
      }
    },
  })
}
export const useGeneratePossibleKeys = (rootId: string) => {
  const api = useApi()
  const invalidatePoller = useInvalidateItemPoller()
  const { t } = useTranslation('legacy', { keyPrefix: 'author.item_actions' })

  return useMutation({
    mutationKey: itemActionKeys.possibleKeys(rootId),
    mutationFn: async (itemKey: string): Promise<{ success: boolean }> => {
      const instance = useContentVersionState.getState().instances[rootId]
      const itemId = itemKey === 'root' ? rootId : itemKey

      try {
        await instance.flush()
        const response = await api.possibleKeys({
          path: { item_id: itemId },
        })

        if (!response.data) {
          console.warn('Failed possible keys')
          return { success: false }
        }

        useContentVersionState.getState().setItem(rootId, itemId, (prev) => ({
          ...prev,
          status: ItemStatusMap.IN_PROGRESS,
        }))

        await invalidatePoller(rootId)

        return { success: Boolean(response.data.ok) }
      } catch (error) {
        console.debug(error, error?.message)
        console.error('generatePossibleKeys error', error?.message, { rootId, itemId })

        addSnack({
          message: t('possible_keys_failed'),
          severity: 'error',
        })
        return { success: false }
      }
    },
  })
}

export const useSaveItem = (rootId: string) => {
  const api = useApi()
  const queryClient = useQueryClient()
  const { t } = useTranslation('legacy', { keyPrefix: 'author.item_actions' })

  return useMutation({
    mutationKey: itemActionKeys.save(rootId),
    mutationFn: async (folderId: string): Promise<{ success: boolean }> => {
      const instance = useContentVersionState.getState().instances[rootId]

      await instance.flush()

      try {
        const response = await api.saveItem({
          path: { item_id: rootId },
          body: { folderId },
        })

        if (response?.error) {
          console.error('Failed to save item: ', response.error)
          throw new Error('Failed to save item')
        }

        const success = !!response.data?.ok

        if (!success) {
          addSnack({
            message: t('save_error'),
            severity: 'error',
          })
          return { success: false }
        }

        queryClient.invalidateQueries({ queryKey: ['author-batches', 'details'] })

        return { success: true }
      } catch (error) {
        console.debug(error, error?.message)
        console.error('saveItem error', error?.message, { rootId })

        addSnack({
          message: t('save_error'),
          severity: 'error',
        })
        return { success: false }
      }
    },
  })
}

export const useCloneItem = (rootId: string) => {
  const api = useApi()
  const queryClient = useQueryClient()
  const { t } = useTranslation('legacy', { keyPrefix: 'author.item_actions' })

  return useMutation({
    mutationKey: itemActionKeys.clone(rootId),
    mutationFn: async (itemIdInput: string): Promise<{ newItemId: string }> => {
      const instance = useContentVersionState.getState().instances[rootId]
      const itemId = itemIdInput === 'root' ? rootId : itemIdInput

      try {
        await instance.flush()
        const response = await api.cloneItem({ path: { item_id: itemId } })

        if (!response.data?.clonedItemId) {
          throw new Error('No clone id returned')
        }

        // Cloning grows the legacy author Batches tab list — refetch it so the
        // newly cloned sibling shows up alongside the source item.
        await queryClient.invalidateQueries({ queryKey: ['author-batches'] })

        return { newItemId: response.data.clonedItemId }
      } catch (error) {
        console.debug(error, error?.message)
        console.error('cloneItem error', error?.message, { rootId, itemId })

        addSnack({
          message: t('clone_failed'),
          severity: 'error',
        })

        throw error
      }
    },
  })
}

export const useGenerateRationales = (rootId: string) => {
  const api = useApi()
  const invalidatePoller = useInvalidateItemPoller()
  const queryClient = useQueryClient()
  const { t } = useTranslation('legacy', { keyPrefix: 'author.rationales' })

  return useMutation({
    mutationKey: itemActionKeys.rationales(rootId),
    mutationFn: async ({
      itemKey,
      keysOnly,
      option,
      generateRationaleFor,
      itemKeyCheck,
    }: GenerateRationalesInput) => {
      const instance = useContentVersionState.getState().instances[rootId]
      const itemId = itemKey === 'root' ? rootId : itemKey

      const context = itemKeyCheck ? 'key_check' : 'rationales'
      try {
        await instance.flush()

        addSnack({
          message: itemKeyCheck ? t('generate_info_key_check') : t('generate_info_rationales'),
          severity: 'info',
          checkboxKey: 'hideRationalesInfo',
        })

        const response = await api.generateRationales({
          path: { item_id: itemId },
          body: {
            keysOnly,
            option,
            generateRationaleFor,
            itemKeyCheck,
          },
        })

        if (!response.data || !response.data.ok) {
          addSnack({
            message: t('generation_failed', { context }),
            severity: 'error',
          })
          return { success: false }
        }

        useContentVersionState.getState().setItem(rootId, itemId, (prev) => ({
          ...prev,
          status: ItemStatusMap.IN_PROGRESS,
        }))

        if (itemKeyCheck) {
          // Optimistically mark the item as PRETEST_KEY/loading so the drawer enters
          // its loading state immediately and the item-poller's refetchInterval keeps
          // polling. Avoids a race where an immediate invalidate refetch returns the
          // pre-transition BFF state and stalls the UI on stale DONE.
          queryClient.setQueryData<GetV1ItemOutput>(itemPollerKeys.item(rootId), (old) =>
            old
              ? {
                  ...old,
                  itemState: {
                    ...old.itemState,
                    status: ItemStatusMap.PRETEST_KEY,
                    isLoading: true,
                  },
                }
              : old,
          )
        } else {
          await invalidatePoller(rootId)
        }

        return { success: Boolean(response.data.ok) }
      } catch (error) {
        console.debug(error, error?.message)
        console.error('generateRationales error', error?.message, { rootId, itemId })

        addSnack({
          message: t('generation_failed', { context }),
          severity: 'error',
        })

        return { success: false }
      }
    },
  })
}

export const useGenerateMoreItems = (rootId: string) => {
  const api = useApi()
  const { t } = useTranslation('legacy', { keyPrefix: 'author.item_actions' })

  return useMutation({
    mutationKey: itemActionKeys.generateMoreItems(rootId),
    mutationFn: async ({
      numberOfItems,
      selectedFlavors,
      selectedCreativity,
    }: GenerateMoreItemsParams) => {
      try {
        const instance = useContentVersionState.getState().instances[rootId]

        await api.generateItemSet({
          body: {
            aiModelId: instance.items.root.aiModel.id,
            submodels: selectedFlavors,
            itemsRequired: parseInt(numberOfItems, 10),
            rootId,
            creativitySlider: mapCreativityToSlider(selectedCreativity),
            temperaturePercentage: selectedCreativity,
          },
        })
      } catch (error) {
        console.debug(error, error?.message)
        console.error('generateMoreItems error', error?.message, { rootId })

        addSnack({
          message: t('add_more_items_error', { error: error.message }),
          severity: 'error',
        })
      }
    },
  })
}

export const useGenerateMath = (rootId: string) => {
  const api = useApi()
  const invalidatePoller = useInvalidateItemPoller()
  const { t } = useTranslation('legacy', { keyPrefix: 'author.item_actions' })

  return useMutation({
    mutationKey: itemActionKeys.generateMath(rootId),
    mutationFn: async (itemId: string): Promise<{ success: boolean }> => {
      const instance = useContentVersionState.getState().instances[rootId]

      try {
        await instance.flush()
        const response = await api.generateMath({
          path: { item_id: itemId },
        })
        if (!response.data) {
          console.warn('Failed generate math')
          return { success: false }
        }
        useContentVersionState.getState().setItem(rootId, itemId, (prev) => ({
          ...prev,
          status: ItemStatusMap.IN_PROGRESS,
        }))
        await invalidatePoller(rootId)

        return { success: Boolean(response.data.ok) }
      } catch (error) {
        console.debug(error, error?.message)
        console.error('generateMath error', error?.message, { rootId, itemId })

        addSnack({
          message: t('generate_math_failed'),
          severity: 'error',
        })
        return { success: false }
      }
    },
  })
}

export const useRunBiasCheck = (rootId: string) => {
  const api = useApi()
  const queryClient = useQueryClient()
  const { t } = useTranslation('legacy', { keyPrefix: 'author.item_actions' })

  return useMutation({
    mutationKey: itemActionKeys.biasCheck(rootId),
    mutationFn: async (itemId: string): Promise<{ success: boolean }> => {
      const instance = useContentVersionState.getState().instances[rootId]

      try {
        await instance.updater.flush()

        const response = await api.runBiasCheck({ path: { item_id: itemId } })

        if (!response.data || !response.data.ok) {
          addSnack({
            message: t('bias_check_failed'),
            severity: 'error',
          })
          return { success: false }
        }

        useContentVersionState.getState().setItem(rootId, itemId, (prev) => ({
          ...prev,
          status: ItemStatusMap.IN_PROGRESS,
        }))

        // Optimistically mark the item as PRETEST_BIAS/loading so the drawer enters
        // its loading state immediately and the item-poller's refetchInterval keeps
        // polling. Avoids a race where an immediate invalidate refetch returns the
        // pre-transition BFF state and stalls the UI on stale DONE.
        queryClient.setQueryData<GetV1ItemOutput>(itemPollerKeys.item(rootId), (old) =>
          old
            ? {
                ...old,
                itemState: {
                  ...old.itemState,
                  status: ItemStatusMap.PRETEST_BIAS,
                  isLoading: true,
                },
              }
            : old,
        )

        return { success: Boolean(response.data.ok) }
      } catch (error) {
        console.debug(error, error?.message)
        console.error('runBiasCheck error', error?.message, { rootId, itemId })

        addSnack({
          message: t('bias_check_failed'),
          severity: 'error',
        })

        return { success: false }
      }
    },
  })
}
